import styled from 'styled-components';
import { Card as AntCard, Button as AntButton, Input as AntInput } from 'antd';

export const Container = styled.div`
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;

  /* Compact shell: the layout column above already subtracted the tab bar, so 100vh
     would push the pinned action row off the bottom of the screen. */
  @media (max-width: 991px) {
    height: 100%;
    max-height: 100%;
  }

  background: linear-gradient(135deg, #1A1D23 0%, #1a1d26 25%, #0f1115 50%, #1a1d26 75%, #1A1D23 100%);
  position: relative;
  padding: 0;
  display: flex;
  flex-direction: column;
  margin: 0;
`;

export const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  position: relative;
  z-index: 1;
  padding: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`;

export const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

export const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

export const Card = styled(AntCard)`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  
  .ant-card-head {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.fontSize.lg};
    font-weight: 600;
    padding: ${({ theme }) => theme.spacing.lg};
  }

  .ant-card-body {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

export const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

export const SectionSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
`;

// Transient prop ($-prefixed) so styled-components doesn't forward `columns`
// onto the underlying <div> (React DOM rejects it with a console warning).
export const Grid = styled.div<{ $columns?: number; $template?: string; $stackOnMobile?: boolean }>`
  display: grid;
  /* minmax(0, 1fr) instead of 1fr so long content (e.g. a UUID in a Select)
     can't blow a column past its share and squeeze its sibling. $template
     overrides the equal split for rows that want uneven columns; the tablet
     media query below still wins, so stacking is unaffected. */
  grid-template-columns: ${({ $template, $columns = 2 }) =>
        $template ?? `repeat(${$columns}, minmax(0, 1fr))`};
  gap: ${({ theme }) => theme.spacing.md};

  /* Input pairs want one per row on a phone, but a row of two short read-only numbers
     (Basis / PnL) only wastes a whole row by stacking — those opt out. */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: ${({ $stackOnMobile = true, $template, $columns = 2 }) =>
        $stackOnMobile ? '1fr' : ($template ?? `repeat(${$columns}, minmax(0, 1fr))`)};
  }
`;

export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }

  /* Inputs match GridTrading's FormItem: same surface, same border, same green
     focus ring. Kept here (not per-field inline styles) so every field in the
     pair config column picks it up. */
  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker {
    background: ${({ theme }) => theme.colors.background.tertiary} !important;
    border-color: ${({ theme }) => theme.colors.border.primary} !important;
    color: ${({ theme }) => theme.colors.text.primary} !important;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary} !important;
    }

    &:focus,
    &.ant-input-focused {
      border-color: ${({ theme }) => theme.colors.primary} !important;
      box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.2) !important;
    }
  }

  .ant-input-number-focused,
  .ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.2) !important;
  }

  /* Grid hides the spinner arrows — they cost width in a narrow column and the
     step is typed, not clicked. */
  .ant-input-number-handler-wrap {
    display: none !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

export const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

export const Input = styled(AntInput)`
  background-color: ${({ theme }) => theme.colors.background.input};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  height: 32px;
  font-size: ${({ theme }) => theme.fontSize.md};

  &:hover, &:focus {
    border-color: ${({ theme }) => theme.colors.border.secondary};
    background-color: ${({ theme }) => theme.colors.background.input};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.placeholder};
  }
`;

export const BuyButton = styled(AntButton)`
  background-color: ${({ theme }) => theme.colors.trading.buy};
  border-color: ${({ theme }) => theme.colors.trading.buy};
  color: #ffffff;
  height: 30px;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  border-radius: 4px;
  padding: 12px 24px;

  &:hover:not(:disabled), &:focus:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.trading.buy} !important;
    border-color: ${({ theme }) => theme.colors.trading.buy} !important;
    opacity: 0.9 !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.trading.buy} !important;
    border-color: ${({ theme }) => theme.colors.trading.buy} !important;
    color: #ffffff !important;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SellButton = styled(AntButton)`
  background-color: ${({ theme }) => theme.colors.trading.sell};
  border-color: ${({ theme }) => theme.colors.trading.sell};
  color: #ffffff;
  height: 30px;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  border-radius: 4px;
  padding: 12px 24px;

  &:hover:not(:disabled), &:focus:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.trading.sell} !important;
    border-color: ${({ theme }) => theme.colors.trading.sell} !important;
    opacity: 0.9 !important;
    color: #ffffff !important;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.trading.sell} !important;
    border-color: ${({ theme }) => theme.colors.trading.sell} !important;
    color: #ffffff !important;
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


export const PriceDisplay = styled.div<{ size?: 'small' | 'medium' | 'large' }>`
  font-family: 'Courier New', monospace;
  font-size: ${({ theme, size = 'medium' }) => 
    size === 'large' ? theme.fontSize.xxl : 
    size === 'medium' ? theme.fontSize.md : 
    theme.fontSize.base
  };
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ChangeDisplay = styled.span<{ positive: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: 600;
  color: ${({ theme, positive }) => 
    positive ? theme.colors.trading.up : theme.colors.trading.down
  };
`;

export const BasisDisplay = styled.div<{ positive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  background-color: ${({ theme, positive }) => 
    positive ? 'rgba(14, 203, 129, 0.1)' : 'rgba(246, 70, 93, 0.1)'
  };
  border: 1px solid ${({ theme, positive }) => 
    positive ? theme.colors.trading.up : theme.colors.trading.down
  };
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 600;
  color: ${({ theme, positive }) => 
    positive ? theme.colors.trading.up : theme.colors.trading.down
  };
`;

export const StatsCard = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

export const StatsLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const StatsValue = styled.div<{ color?: 'buy' | 'sell' | 'warning' | 'neutral' }>`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 600;
  color: ${({ theme, color = 'neutral' }) => {
    switch (color) {
      case 'buy': return theme.colors.trading.buy;
      case 'sell': return theme.colors.trading.sell;
      case 'warning': return theme.colors.trading.warning;
      default: return theme.colors.text.primary;
    }
  }};
`;

export const TableWrapper = styled.div`
  /* Never wider than its card: a table wider than the card scrolls inside itself. Growing
     to the table's max-content pushed the card's right-hand columns out of reach. */
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  /* Pair tables sit on its cards. */
  --table-surface: ${({ theme }) => theme.colors.background.secondary};

  .ant-table {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .ant-table-thead > tr > th {
    color: ${({ theme }) => theme.colors.text.secondary};
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.fontSize.base};
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }

  /* An editable price cell aligns with the read-only ones below it. antd's column align
     styles the td, not the input inside it, so without this the typed target price sits
     left of the equity leg's target price and the two legs stop lining up. */
  .leg-price-input .ant-input-number-input {
    text-align: right;
    padding-right: 0;
  }

  .ant-table-summary > tr > td {
    background-color: rgba(255, 255, 255, 0.02);
    border-top: 1px solid ${({ theme }) => theme.colors.border.secondary};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

export const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: rgba(246, 70, 93, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.trading.sell};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.trading.sell};
  font-size: ${({ theme }) => theme.fontSize.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const SuccessMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: rgba(14, 203, 129, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.trading.buy};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.trading.buy};
  font-size: ${({ theme }) => theme.fontSize.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// Grid Layout Components
export const GridLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 400px;
  gap: ${({ theme }) => theme.spacing.sm};
  height: calc(100vh - 250px);
  min-height: calc(100vh - 250px);
  margin-right: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 600px 300px;
    height: auto;
    margin-right: 0;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  height: 100%;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: auto;
    overflow: visible;
  }
`;

export const CenterColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

export const FullWidthLogSection = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-left: 0;
  margin-right: 0;
`;

export const ChartContainer = styled.div<{ $height?: number }>`
  width: 100%;
  height: ${({ $height = 300 }) => $height > 0 ? `${$height}px` : '100%'};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  overflow: hidden;
`;

export const CompactCard = styled(Card)`
  .ant-card-head {
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    min-height: 36px;
    
    .ant-card-head-title {
      font-size: ${({ theme }) => theme.fontSize.sm};
      padding: 0;
    }
  }
  
  .ant-card-body {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const BasisCard = styled.div<{ $height?: number }>`
  width: 100%;
  height: ${({ $height }) => $height ? `${$height}px` : '100%'};
  min-width: 0;
  min-height: 0;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
`;

export const BasisLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const BasisValueLarge = styled.div<{ $positive: boolean }>`
  font-size: 48px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  color: ${({ theme, $positive }) => 
    $positive ? theme.colors.trading.buy : theme.colors.trading.sell
  };
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  line-height: 1;
`;

export const BasisChange = styled.div<{ $positive: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  color: ${({ theme, $positive }) => 
    $positive ? theme.colors.trading.buy : theme.colors.trading.sell
  };
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const CompactStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const CompactStatItem = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CompactStatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const CompactStatValue = styled.div<{ $color?: 'buy' | 'sell' | 'warning' | 'neutral' }>`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  color: ${({ theme, $color = 'neutral' }) => {
    switch ($color) {
      case 'buy': return theme.colors.trading.buy;
      case 'sell': return theme.colors.trading.sell;
      case 'warning': return theme.colors.trading.warning;
      default: return theme.colors.text.primary;
    }
  }};
`;

export const ScrollableTableContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.secondary};
    border-radius: 3px;
  }
`;

export const StocksTableCard = styled(CompactCard)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  
  .ant-card-head {
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
    min-height: 36px;
    
    .ant-card-head-title {
      font-size: ${({ theme }) => theme.fontSize.sm};
      padding: 0;
    }
  }
  
  .ant-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

export const ProportionInput = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  .ant-input-number {
    flex: 1;
    background: transparent;
    border: none;
    
    input {
      background: transparent;
      color: ${({ theme }) => theme.colors.text.primary};
    }

    &:hover, &:focus {
      border: none;
      box-shadow: none;
    }
  }
`;

export const ProportionLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  white-space: nowrap;
  min-width: 80px;
`;

export const ProportionValue = styled.span<{ $warning?: boolean }>`
  color: ${({ theme, $warning }) => 
    $warning ? theme.colors.warning : theme.colors.trading.buy};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  min-width: 60px;
  text-align: right;
`;

export const StockRow = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $selected }) => 
    $selected ? theme.colors.background.secondary : theme.colors.background.primary};
  border: 1px solid ${({ theme, $selected }) => 
    $selected ? theme.colors.primary : theme.colors.border.primary};
  border-radius: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const EditProportionButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  /* Icon-only, inside the Symbol cell: 12px of side padding around a 14px glyph is
     what made that column read as mostly empty space. */
  padding: 2px 6px;
  line-height: 1;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const PortfolioSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  position: relative;
  z-index: 1;
`;

export const PortfolioLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  white-space: nowrap;
`;

export const PortfolioSelect = styled.div`
  flex: 1;
  max-width: 400px;

  .ant-select {
    width: 100%;
  }

  .ant-select-selector {
    background: ${({ theme }) => theme.colors.background.primary} !important;
    border: 1px solid ${({ theme }) => theme.colors.border.primary} !important;
    color: ${({ theme }) => theme.colors.text.primary} !important;
    
    &:hover {
      border-color: ${({ theme }) => theme.colors.primary} !important;
    }
  }

  .ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const PortfolioInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

export const PortfolioStatusBadge = styled.span<{ $status: 'active' | 'inactive' | 'closed' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  text-transform: uppercase;
  background: ${({ theme, $status }) => {
    switch ($status) {
      case 'active':
        return 'rgba(14, 203, 129, 0.1)';
      case 'inactive':
        return 'rgba(252, 213, 53, 0.1)';
      case 'closed':
        return 'rgba(246, 70, 93, 0.1)';
      default:
        return theme.colors.background.tertiary;
    }
  }};
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'active':
        return theme.colors.trading.buy;
      case 'inactive':
        return theme.colors.warning;
      case 'closed':
        return theme.colors.trading.sell;
      default:
        return theme.colors.text.secondary;
    }
  }};
  border: 1px solid ${({ theme, $status }) => {
    switch ($status) {
      case 'active':
        return theme.colors.trading.buy;
      case 'inactive':
        return theme.colors.warning;
      case 'closed':
        return theme.colors.trading.sell;
      default:
        return theme.colors.border.primary;
    }
  }};
`;


/**
 * The pair config column. Fields the user sets carry `pair-edit-input`: a raised, bordered
 * box, so a typed value is never mistaken for a readout. `is-missing` marks a required
 * field still empty.
 */
export const PairConfigScroll = styled.div`
  .pair-edit-input.ant-input-number {
    background: #2B3139;
    border: 1px solid #3C434D;
    border-radius: 4px;
  }
  .pair-edit-input.ant-input-number:hover {
    border-color: #5E6673;
  }
  .pair-edit-input.ant-input-number-focused {
    border-color: #0ECB81;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.12);
  }
  .pair-edit-input.is-missing.ant-input-number {
    border-color: rgba(246, 70, 93, 0.6);
  }
  .pair-edit-input.is-missing .ant-input-number-input::placeholder {
    color: rgba(246, 70, 93, 0.75);
  }
  /* A secondary action inside a muted line: neutral until hovered, not antd's link blue. */
  .ant-btn.pair-subtle-link {
    color: #B7BDC6;
    text-decoration: underline;
    text-decoration-color: #5E6673;
    text-underline-offset: 2px;
  }
  .ant-btn.pair-subtle-link:hover {
    color: #EAECEF;
    text-decoration-color: #EAECEF;
  }
  /* Locked while the bot runs: flat, so "locked" and "yours to edit" differ. */
  .pair-edit-input.ant-input-number-disabled {
    background: transparent;
    border-color: ${({ theme }) => theme.colors.border.primary};
  }
`;

/**
 * The two leg tables. Fixed layout at the panel's own width: four columns that fit, so no
 * field sits behind a horizontal scroll and nothing needs clipping.
 */
export const LegTableWrapper = styled(TableWrapper)`
  overflow-x: visible;

  /* The leg headers keep their own band: the tertiary fill, column dividers, 12px semibold.
     Doubled class to outrank the app-wide header theme. */
  && .ant-table-wrapper .ant-table .ant-table-thead > tr > th {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.fontSize.base};
    font-weight: 600;
    letter-spacing: normal;
    /* wrap between words only: "Weight" never splits as "Weigh / t" */
    overflow-wrap: normal;
    word-break: keep-all;
  }
  && .ant-table-wrapper .ant-table .ant-table-thead > tr > th:not(:last-child)::before {
    background-color: ${({ theme }) => theme.colors.border.secondary};
  }

  .ant-table table {
    table-layout: fixed;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 6px;
  }

  .leg-price-input .ant-input-number-input {
    padding-right: 6px;
  }
`;
